import React, { useState } from 'react';
import { getServiceTypeImage } from '../ServiceTypeSelect/ListItem';
import { ServiceType } from '@bookpeazy/common-js';
import Link from 'next/link';
import ServiceProviderAvatarWithNameAndRating from '../ServiceProviderAvatarWithNameAndRating';
import { UseServiceProviders } from 'Common/hooks/useServiceProviders';
import ServiceProviderPhotoGalleryCarousel from '../ServiceProviderPhotoGalleryCarousel';
import Phone from 'Images/svg/phone.svg';
import List from 'Images/svg/list.svg';
import MapPin from 'Images/svg/map-pin.svg';
import { parsePhoneNumber } from 'react-phone-number-input';
import ServiceOfferingSelectCategorized from '../ServiceOfferingSelectCategorized';
import Clock from 'Images/svg/clock.svg';
import ChevronDown from 'Images/svg/chevron-down.svg';
import { Collapse } from 'antd';
import ServiceProviderAvailabilityTable from '../ServiceProviderAvailabilityTable';
import { formatMoney } from 'Common/functions/Money';
import DiscountRulesDetailsPopover from '../DiscountRulesDetailsPopover';

type ServiceProvider = Pick<
  UseServiceProviders[number],
  'displayName' | 'serviceType' | 'serviceOfferings' | 'reviewCount' | 'averageRating' | 'dayOfWeekAvailability' | 'serviceOfferingCategories' | 'isApproved' | 'discountRules'
> & {
  profile?: Pick<NonNullable<UseServiceProviders[number]['profile']>, 'avatarImage' | 'bannerImage' | 'photoGallery' | 'servicesCopy' | 'introVideoURL'> | null
};

type ServiceProviderListItemProps = {
  serviceProvider: ServiceProvider;
  link: string;
  hideSelectButton?: boolean;
  getSelectedServiceOfferingLink: (souid: string) => string;
  phoneNumber: string;
};

const ServiceProviderListItem: React.FC<ServiceProviderListItemProps> = ({
  serviceProvider,
  link,
  hideSelectButton,
  getSelectedServiceOfferingLink,
  phoneNumber,
}) => {
  const { profile, serviceOfferings, serviceOfferingCategories } = serviceProvider;
  const defaultAvatarImageSrc =
    getServiceTypeImage(serviceProvider.serviceType as ServiceType);

  const defaultServiceOffering = serviceOfferings.length > 0 ? serviceOfferings[0].uid : undefined;
  const [selectedOfferingUid, setSelectedOfferingUid] = useState(defaultServiceOffering);

  const photoGallery = profile?.photoGallery &&
    profile.photoGallery.items.length > 0 ?
    profile.photoGallery.items :
    null;
  const bannerImage = profile?.bannerImage ?
    profile.bannerImage.uploadedFile.publicUrl :
    null;

  const listItem = (
    <div
      className="w-full mb-8 flex sm:gap-8 lg:gap-20 sm:flex-col lg:flex-row lg:justify-between"
    >
      <div
        className="flex max-h-[512px] sm:w-full sm:min-w-full lg:w-1/2 lg:min-w-[50%]"
      >
        <ServiceProviderPhotoGalleryCarousel
          photos={photoGallery}
          bannerImageSrc={bannerImage}
          introVideoUrl={profile?.introVideoURL || null}
        />
      </div>
      <div
        className="flex flex-col gap-4 lg:w-1/2"
      >
        <Link
          href={link}
          className="w-full"
        >
          <ServiceProviderAvatarWithNameAndRating
            serviceProviderAvatarImageUrl={profile?.avatarImage?.uploadedFile.publicUrl ||
              defaultAvatarImageSrc}
            displayName={serviceProvider.displayName}
            averageRating={serviceProvider.averageRating}
            reviewCount={serviceProvider.reviewCount}
            hideServesLocalAreaCopy={true}
            isVerified={serviceProvider.isApproved}
          />
        </Link>

        <div className="flex gap-4 justify-between">
          {(serviceOfferings.length > 0 || serviceOfferingCategories.length > 0) && (
            <ServiceOfferingSelectCategorized
              value={selectedOfferingUid}
              onSelectServiceOffering={offering => setSelectedOfferingUid(offering.uid)}
              serviceOfferingCategories={[
                ...serviceOfferingCategories,
              ]}
              serviceOfferings={serviceOfferings}
            >
              {hideSelectButton || selectedOfferingUid && (
                <Link
                  href={getSelectedServiceOfferingLink(selectedOfferingUid)}
                  className="btn-primary text-primary bg-transparent hover:bg-gray-100 text-center flex items-center justify-center text-lg py-4 border-2 border-primary border-solid basis-1/3 sm:w-full lg:w-1/3"
                >
                  Select
                </Link>
              )}
            </ServiceOfferingSelectCategorized>
          )}
        </div>

        <div
          className="lg:w-1/2"
        >
          <DiscountRulesDetailsPopover
            discountRules={serviceProvider.discountRules}
            serviceProviderDisplayName={serviceProvider.displayName}
            serviceProviderAvatarImageUrl={serviceProvider.profile?.avatarImage?.uploadedFile.publicUrl}
            mode="arrow"
          />
        </div>

        <Collapse
          className="lg:w-1/2"
          ghost
          expandIconPosition='right'
          expandIcon={() => (<></>)}
          items={[{
            key: '1',
            label: (
              <div className="flex gap-6 items-baseline">
                <span className="flex gap-2 items-baseline">
                  <Clock />
                  Hours Of Operation
                </span>
                <ChevronDown className="stroke-black" />
              </div>
            ),
            children: (
              <ServiceProviderAvailabilityTable
                dayOfWeekAvailability={serviceProvider.dayOfWeekAvailability}
              />
            )
          }]}
        >
        </Collapse>

        <div className="flex flex-col gap-2 items-start">
          <a
            href={`tel:${phoneNumber}`}
            className="flex items-center gap-2"
          >
            <Phone /> {parsePhoneNumber(phoneNumber)?.formatNational() || phoneNumber}
          </a>

          <div className="flex items-start gap-2">
            {profile?.servicesCopy?.length ? (
              <Collapse
                className="w-1/2"
                ghost
                expandIconPosition='right'
                expandIcon={() => (<></>)}
                items={[{
                  key: '1',
                  label: (
                    <div className="flex gap-6 items-center">
                      <span className="flex gap-2 items-baseline">
                        <List className="mt-1" />
                        Services
                      </span>
                      <ChevronDown className="stroke-black" />
                    </div>
                  ),
                  children: (
                    <table className="min-w-full divide-y divide-gray-200 my-4">
                      <thead className="bg-paleOrange">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Service
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {profile.servicesCopy.map((so, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {so.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-black">
                              {formatMoney(so.price)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ),
                }]}
              />
            ) : (
              <div className="flex items-baseline gap-2">
                <List className="mt-1" />
                <div>
                  <h4 className="inline font-bold">
                    Services:&nbsp;
                  </h4>
                  <p className="text-wrap">
                    {serviceOfferings.map(
                      (so, index) => `${so.title}${index < serviceOfferings.length - 1 ? ', ' : ''}`
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <MapPin className="fill-primary" />
            <span>
              Winnipeg and surrounding areas
            </span>
          </div>
        </div>

        <a
          className="btn-primary text-center flex items-center justify-center text-lg py-4 sm:w-full lg:w-2/5"
          href={link}
        >
          Learn More
        </a>

      </div>
    </div>
  );

  return listItem;
};

export default ServiceProviderListItem;

